import React, { useEffect, useState } from "react";
import NavbarPage from "../../components/Navbar/Navbar_Page";
import Footer from "../../components/Footer/footer";
import "../Index10/Index10.css";
import "./Legal.css";
import "./Faq.css";

const FAQ_ITEMS = [
  {
    category: "Getting Started",
    questions: [
      {
        q: "What is Public Backlog?",
        a: "Public Backlog is a feedback management platform that lets you collect bug reports, feature requests, ideas, and questions from your customers. You can organize feedback with kanban boards, prioritize with voting, share public roadmaps, and sync everything to the tools you already use.",
      },
      {
        q: "How do I create an account?",
        a: "Head to app.publicbacklog.com and sign up with your email address or Google account. Once registered, you can create an Organization and set up your first Backlog in under two minutes.",
      },
      {
        q: "Is there a free plan?",
        a: "Yes. The free Starter plan includes core features like feedback collection, voting, a public board, and basic integrations. You can upgrade anytime if you need more backlogs, team members, or advanced features like custom fields and SSO.",
      },
      {
        q: "Can I try paid features before committing?",
        a: "All new accounts start with full access to paid features during a trial period. No credit card required. When the trial ends you can choose a plan or stay on the free tier.",
      },
    ],
  },
  {
    category: "Feedback & Tickets",
    questions: [
      {
        q: "What types of feedback can I collect?",
        a: "Public Backlog supports five ticket types: Bug Reports, Feature Requests, Ideas, Questions, and a general All category. Each type has its own icon and color coding so you can tell them apart at a glance.",
      },
      {
        q: "Can customers submit feedback without an account?",
        a: "That depends on your settings. You can allow anonymous submissions, require just an email address, or require users to create an Organization User account. It's configurable per-organization.",
      },
      {
        q: "How does voting work?",
        a: "Users can upvote or downvote tickets. Vote counts help you see which issues and requests matter most to your customers. High-vote items naturally rise to the top when sorting by popularity.",
      },
      {
        q: "What is duplicate detection?",
        a: "When someone submits new feedback, Public Backlog suggests similar existing tickets so they can upvote instead of creating a duplicate. This keeps your board clean and consolidates feedback automatically.",
      },
      {
        q: "Can I set priorities on tickets?",
        a: "Yes. Tickets support five priority levels: None, Low, Medium, High, and Critical. Admins can set and update priorities from the ticket detail view or the kanban board.",
      },
    ],
  },
  {
    category: "Managing Your Board",
    questions: [
      {
        q: "What is the kanban board?",
        a: "The kanban board gives you a drag-and-drop view of all tickets organized by status: New, Approved, In Progress, Done, and Rejected. Drag a ticket between columns to update its status instantly.",
      },
      {
        q: "Can I share a public roadmap?",
        a: "Absolutely. You can create named roadmaps, add tickets to them, and share a public read-only view with your customers. It's a great way to show what you're working on and build transparency.",
      },
      {
        q: "What are internal notes?",
        a: "Internal notes are comments visible only to Organization administrators. Use them to discuss tickets privately without your customers seeing the conversation.",
      },
      {
        q: "How does the changelog work?",
        a: "When you ship a feature or fix a bug, create a changelog entry and link it to the tickets that inspired it. Your customers can see what shipped and feel heard — closing the feedback loop.",
      },
      {
        q: "Can I use custom fields?",
        a: "Yes. Admins can define custom fields (text, number, dropdown, or checkbox) per backlog. These appear on the ticket submission form and help you capture structured data specific to your needs.",
      },
    ],
  },
  {
    category: "Integrations",
    questions: [
      {
        q: "Which integrations are supported?",
        a: "Public Backlog integrates with 19+ tools including GitHub, Jira, Linear, Asana, Slack, Microsoft Teams, Intercom, Zendesk, Salesforce, HubSpot, Azure DevOps, ClickUp, Notion, Help Scout, Freshdesk, Zapier, Segment, Discord, and outgoing webhooks.",
      },
      {
        q: "Is the sync two-way?",
        a: "For project management tools like GitHub, Jira, Linear, Asana, Azure DevOps, and ClickUp — yes. Changes made in either system are synced back. Communication tools like Slack and Teams are outbound notifications.",
      },
      {
        q: "How do I connect an integration?",
        a: "Go to your Organization's admin settings and click Integrations. Most integrations use OAuth — just click Connect and authorize. A few (like Freshdesk and Discord) use API keys which you paste into the settings form.",
      },
      {
        q: "Can I use Zapier to connect other tools?",
        a: "Yes. The Zapier integration supports four event triggers (ticket created, status changed, comment added, vote registered) so you can push feedback data to any of Zapier's 5,000+ connected apps.",
      },
      {
        q: "What about custom webhooks?",
        a: "You can configure outgoing webhooks per-organization. Events are signed with HMAC-SHA256 so your receiving server can verify authenticity. You also get a delivery log to debug any issues.",
      },
    ],
  },
  {
    category: "Security & Privacy",
    questions: [
      {
        q: "How is my data protected?",
        a: "Passwords are cryptographically hashed, authentication uses secure HttpOnly cookies over HTTPS, and all integration credentials are encrypted at rest. We use role-based access control to limit data access within organizations.",
      },
      {
        q: "Do you support Single Sign-On (SSO)?",
        a: "Yes. Public Backlog supports OIDC-based SSO with providers like Okta, Microsoft Entra ID, Auth0, and Google Workspace. Admins can enforce SSO and restrict login to specific email domains.",
      },
      {
        q: "Where is my data stored?",
        a: "Your data is stored on servers in the United States. File uploads (avatars and ticket attachments) are stored in S3-compatible object storage. For more details, see our Privacy Policy.",
      },
      {
        q: "Can I delete my account and data?",
        a: "Yes. Contact us at support@publicbacklog.com to request account deletion. We'll remove your personal data in accordance with our Privacy Policy. See our Terms of Service for details on data retention.",
      },
    ],
  },
  {
    category: "Billing",
    questions: [
      {
        q: "How does billing work?",
        a: "Paid plans are billed monthly or annually through Stripe. We never store your credit card details — Stripe handles all payment processing securely (PCI DSS Level 1 compliant).",
      },
      {
        q: "Can I change plans or cancel anytime?",
        a: "Yes. You can upgrade, downgrade, or cancel your subscription at any time from your Organization settings. If you cancel, you keep access to paid features through the end of your billing period.",
      },
      {
        q: "Do you offer refunds?",
        a: "Subscription fees are generally non-refundable. If you feel there are exceptional circumstances, reach out to support@publicbacklog.com and we'll work with you.",
      },
    ],
  },
];

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? "faq-item-open" : ""}`} onClick={() => setOpen(!open)}>
      <div className="faq-question">
        <span>{q}</span>
        <span className="faq-chevron">{open ? "\u2212" : "\u002B"}</span>
      </div>
      {open && <div className="faq-answer">{a}</div>}
    </div>
  );
};

const Faq = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="home-ten legal-page">
      <NavbarPage navItems={[]} navClass="" />
      <div className="legal-content">
        <h1>Frequently Asked Questions</h1>
        <p className="legal-updated">
          Can't find what you're looking for? Reach out at{" "}
          <a href="mailto:support@publicbacklog.com">support@publicbacklog.com</a>
        </p>

        {FAQ_ITEMS.map((section, i) => (
          <div key={i} className="faq-section">
            <h2>{section.category}</h2>
            {section.questions.map((item, j) => (
              <FaqItem key={j} q={item.q} a={item.a} />
            ))}
          </div>
        ))}
      </div>
      <Footer />
    </div>
  );
};

export default Faq;
